import { PARTS, SLOTS, partById, partDesc, partName, partsOfSlot, upCost } from '../data/parts';
import { MECHS } from '../entities/mechs';
import { $ } from '../platform/dom';
import { SFX } from '../platform/audio';
import { META, MECH_KILLS } from '../platform/storage';
import { buyPart, curMech, ensureParts, equipPart, equippedId, ownedLevel, upgradePart } from '../sim/meta-parts';
import { P, equipMech } from '../world/player';
import { RUN_CFG } from './screens/brief';
import { ROWS, ruleDiff, specOf } from './hangar-stats';
/* ============================================================
   機庫 · 面板內容
   ============================================================

   三欄：**部位導航 → 部件清單 → 整機規格**，資訊由粗到細。
   舊版把六個部位、三個選項、動作按鈕全部塞進同一片右側面板，
   三層的字級與底色又太接近，掃視時分不出主次。

   ## 選中與比較

   點一張部件卡片 = 選中它，**下面的規格區立刻顯示「換過去會變怎樣」**：

     未持有／未裝備 → 比較「裝上它」
     已裝備且未滿級 → 比較「升到下一級」

   沒有這個比較，玩家做不出取捨判斷（支柱五第 2 條），
   而「重裝駕艙 +24 結構 −3% 移速」到底划不划算，只有看到兩邊的實際值才答得出來。 */

/** 左欄最上面那一格的 id。它不是部位，是**機體**（使用者 2026-08-05 指定
    「機庫也要能切換機甲」）。用同一套導航是刻意的：左欄本來就是「切換什麼」，
    機體只是最外面那一層，不需要新的互動模型。 */
export var UNIT = 'unit';
export var selSlot = 'core';
var selPart: string | null = null;

export function setSelSlot(id) { selSlot = id; selPart = equippedId(id); }

/* 切部位時要把鏡頭帶過去，但那是 `hangar.ts` 的事，而它已經 import 了這個檔 ——
   直接反向 import 會形成循環。用註冊點把方向掰直。 */
/** 目前機體的主武裝 key。MAIN 位的部件名稱／說明要跟著它換
    （`data/parts.ts` 的 MAIN_ALT）。機庫一定已經 equipMech 過，
    但標題頁直接開機庫的路徑上 `P.mech` 可能還沒設，退回預設。 */
function primaryOf() { return (P.mech && P.mech.primary) || 'vulcan'; }

var slotHandler: ((id: string) => void) | null = null;
export function setSlotHandler(fn) { slotHandler = fn; }

/** 換部件之後重建預覽 —— `startRun` 每場會重建，但機庫的 rig 是開機時建的，
    不自己重建一次就看不到剛換上去的東西（玩家會以為換了沒用）。 */
function refreshRig() { equipMech(P.mech ? P.mech.id : 'chassis0'); }

function dots(lv, max) {
  var s = '';
  for (var i = 1; i <= max; i++) s += '<i class="' + (i <= lv ? 'on' : '') + '"></i>';
  return '<span class="hdots">' + s + '</span>';
}

/* ---------- 左欄：六個部位 ---------- */
function renderNav() {
  var m = P.mech || MECHS.chassis0;
  var html = '<button class="hnav-item hnav-unit' + (selSlot === UNIT ? ' sel' : '') +
    '" data-s="' + UNIT + '"><em>UNIT</em><b>' + m.name + '</b>' +
    '<span class="hnav-cn">' + m.cn + '</span></button><div class="hnav-sep"></div>';
  for (var i = 0; i < SLOTS.length; i++) {
    var s = SLOTS[i], eq = partById(equippedId(s.id)), lv = eq ? ownedLevel(eq.id) : 0;
    html += '<button class="hnav-item' + (selSlot === s.id ? ' sel' : '') + '" data-s="' + s.id + '">' +
      '<em>' + s.en + '</em>' +
      '<b>' + (eq ? partName(eq, primaryOf()) : '—') + '</b>' +
      dots(lv, 5) + '</button>';
  }
  $('hNav').innerHTML = '<div class="hnav-head"><span>機庫</span><small>HANGAR</small></div>' + html;
  bind('hNav', '.hnav-item', 'data-s', function (id) {
    setSelSlot(id); render();
    if (slotHandler) slotHandler(id);      // 鏡頭帶到該部位
  });
}

/* ---------- 右欄：部件清單 + 規格 ---------- */
function renderDetail() {
  if (selSlot === UNIT) return renderMechs();
  var slot = SLOTS.filter(function (s) { return s.id === selSlot; })[0];
  var list = partsOfSlot(selSlot);
  var sp = selPart ? partById(selPart) : null;
  if (!sp || sp.slot !== selSlot) selPart = equippedId(selSlot);

  var prim = primaryOf();
  var html = '<div class="hdet-head"><em>' + slot.en + '</em><h2>' + slot.n +
    '</h2><small>' + slot.hint + '</small></div><div class="hplist">';

  for (var k = 0; k < list.length; k++) {
    var p = list[k], lv = ownedLevel(p.id), on = equippedId(p.slot) === p.id;
    var cls = 'hpart' + (on ? ' on' : '') + (lv === 0 ? ' locked' : '') + (selPart === p.id ? ' sel' : '');
    var tag = on ? '<span class="tag on">已裝備</span>'
      : lv > 0 ? '<span class="tag">已持有</span>'
        : '<span class="tag off">' + p.cost + ' 廢料</span>';
    html += '<button class="' + cls + '" data-p="' + p.id + '">' +
      '<div class="hp-row"><b>' + partName(p, prim) + '</b>' + tag + '</div>' +
      (lv > 0 ? dots(lv, 5) : '<span class="hdots muted">未持有</span>') +
      '<p>' + partDesc(p, Math.max(1, lv), prim) + '</p></button>';
  }
  html += '</div>' + renderActions() + renderSpec();
  $('hDetail').innerHTML = html;

  bind('hDetail', '.hpart', 'data-p', function (id) { selPart = id; render(); });
  bind('hDetail', '[data-buy]', 'data-buy', function (id) { act(buyPart(id), '廢料不足'); });
  bind('hDetail', '[data-up]', 'data-up', function (id) { act(upgradePart(id), '廢料不足'); });
  bind('hDetail', '[data-eq]', 'data-eq', function (id) { act(equipPart(id), '尚未購買'); });
}

/** 動作列：**同一時間只有一個主要動作**。舊版每張卡片各自掛兩顆按鈕，
    畫面上同時出現六顆，玩家反而不知道該按哪一顆。 */
function renderActions() {
  var p = partById(selPart);
  if (!p) return '';
  var lv = ownedLevel(p.id), on = equippedId(p.slot) === p.id, scrap = META.scrap || 0;
  var out = '<div class="hact">';
  if (lv === 0) {
    out += btn('data-buy="' + p.id + '"', '購買', p.cost, scrap < p.cost, 'buy');
  } else {
    if (!on) out += btn('data-eq="' + p.id + '"', '裝備', null, false, 'eq');
    if (lv < 5) {
      var c = upCost(lv + 1);
      out += btn('data-up="' + p.id + '"', '升級 Lv' + (lv + 1), c, scrap < c, '');
    } else out += '<span class="hact-max">已達最高等級</span>';
  }
  return out + '</div>';
}
function btn(attr, label, cost, dis, cls) {
  return '<button class="hbtn ' + cls + '"' + attr + (dis ? ' disabled' : '') + '>' +
    label + (cost != null ? '<span class="cost">' + cost + '</span>' : '') + '</button>';
}

/** 規格區：目前值 →（選中的部件生效後）新值。沒有差異就只顯示目前值。 */
function renderSpec() {
  var now = specOf(), p = partById(selPart);
  var next: any = now, note = '';
  if (p) {
    var lv = ownedLevel(p.id), on = equippedId(p.slot) === p.id;
    if (!on) { next = specOf(p.slot, p.id); note = '裝備後'; }
    else if (lv < 5) { next = specOf(p.slot, p.id, lv + 1); note = '升級後'; }
  }
  var rows = '';
  for (var i = 0; i < ROWS.length; i++) {
    var r = ROWS[i], a = now[r.k] as number, b = next[r.k] as number;
    var d = b - a, cls = Math.abs(d) < 0.0001 ? '' : (d > 0 ? ' up' : ' down');
    rows += '<div class="hspec-row' + cls + '"><span>' + r.n + '</span>' +
      '<b>' + r.fmt(a) + '</b>' +
      (cls ? '<i>' + r.fmt(b) + '</i>' : '<i class="same"></i>') + '</div>';
  }
  var rules = ruleDiff(now, next);
  var changed = rows.indexOf('hspec-row up') >= 0 || rows.indexOf('hspec-row down') >= 0 || rules.length > 0;
  return '<div class="hspec"><div class="hspec-head">整機規格' +
    (changed && note ? '<small>' + note + '</small>' : '') + '</div>' +
    '<div class="hspec-grid">' + rows + '</div>' +
    (rules.length ? '<ul class="hrules"><li>' + rules.join('</li><li>') + '</li></ul>' : '') +
    '</div>';
}

/* ---------- 底部 ---------- */
function renderFoot() {
  /* **這台機體**持有幾個 —— 兩台各自一套（`sim/meta-parts.ts`），
     顯示全域總數會讓玩家以為切過去就有得用。 */
  var owned = PARTS.filter(function (p) { return ownedLevel(p.id) > 0; }).length;
  $('hScrap').textContent = String(META.scrap || 0);
  $('hOwned').textContent = owned + ' / ' + PARTS.length;
  var m = P.mech || MECHS.chassis0;
  var nm = $('hMechName');
  if (nm) {
    /* 名稱裡的編號染成琥珀色（`styles/hangar.css` 的 `.hstage-title b em`）。
       切在最後一個連字號，所以 CHASSIS-0 與 LANCER-2 都對。 */
    var i = m.name.lastIndexOf('-');
    nm.innerHTML = i > 0 ? m.name.slice(0, i) + '<em>' + m.name.slice(i) + '</em>' : m.name;
  }
  var cn = $('hMechCn'); if (cn) cn.textContent = m.cn;
}

/* ---------- 右欄（機體模式）：可切換的機體卡片 ---------- */
function renderMechs() {
  var cur = curMech();
  var ids = Object.keys(MECHS);
  var html = '<div class="hdet-head"><em>UNIT</em><h2>機體</h2>' +
    '<small>每台機體的部件各自持有、各自升級</small></div><div class="hplist">';
  for (var k = 0; k < ids.length; k++) {
    var m: any = MECHS[ids[k]], on = ids[k] === cur;
    var un = META.mechs.indexOf(ids[k]) >= 0;
    var cls = 'hpart hmech' + (on ? ' on' : '') + (un ? '' : ' locked');
    var tag = on ? '<span class="tag on">已裝備</span>'
      : un ? '<span class="tag">已解鎖</span>'
        /* 門檻寫在 `platform/storage.ts` 的 `checkUnlocks()`，這裡只是顯示。
           **不要寫死一個數字** —— 第三台的門檻是 900，寫死 400 的話
           玩家會在 400 擊墜時盯著一張永遠不開的卡片。 */
        : '<span class="tag off">累計擊墜 ' + (MECH_KILLS[ids[k]] || 400) + '</span>';
    /* 部件持有數放在卡片上：切過去之前就看得到「那台是不是還光著」，
       不然玩家換過去才發現六個部位全是起手件。 */
    var got = PARTS.filter(function (p) { return ownedLevel(p.id, ids[k]) > 0; }).length;
    html += '<button class="' + cls + '"' + (un ? ' data-m="' + ids[k] + '"' : ' disabled') + '>' +
      '<div class="hp-row"><b>' + m.name + '</b>' + tag + '</div>' +
      '<span class="hmech-cn">' + m.cn + '</span>' +
      '<p>' + m.desc + '</p>' +
      '<div class="hmech-kit">' + m.icons.map(function (x) { return '<i>' + x + '</i>'; }).join('') + '</div>' +
      '<div class="hmech-row"><span>結構 <b>' + m.hp + '</b></span>' +
      '<span>護盾 <b>' + m.sh + '</b></span>' +
      '<span>速度 <b>' + m.speed + '</b></span>' +
      '<span>推進 <b>' + m.thrustMax + '</b></span></div>' +
      (un ? '<div class="hmech-own">持有部件 ' + got + ' / ' + PARTS.length + '</div>' : '') +
      '</button>';
  }
  html += '</div>' + renderSpec();
  $('hDetail').innerHTML = html;
  bind('hDetail', '[data-m]', 'data-m', function (id) {
    if (id === curMech()) return;
    /* **順序有意義**：先 ensureParts 讓新機體有起手件，再 equipMech ——
       `m.build()` 會去讀那台的 `META.equip`，反過來的話第一次切過去
       會拿不到裝備、外形全部退回起手件（而且沒有任何錯誤）。 */
    ensureParts(id);
    equipMech(id);
    /* **出擊讀的是 `RUN_CFG.mech`，不是 `P.mech`。**
       只 equipMech 的話機庫裡換好了、按出擊卻還是用簡報頁最後選的那台
       （使用者 2026-08-06 回報）。兩個畫面都要寫同一個欄位，
       它才是「這一場要開哪台」的單一事實來源。 */
    RUN_CFG.mech = id;
    SFX.level();
    render();
  });
}

export function renderHangar() { render(); }

function render() {
  renderNav(); renderDetail(); renderFoot();
}

function act(ok, failMsg) {
  if (!ok) { flash(failMsg); return; }
  SFX.level();
  /* 買 / 升 / 裝**都要重建 rig**：升級也可能改變外形，
     而重建的成本遠低於「玩家以為沒生效」的代價。 */
  refreshRig();
  render();
}

var flashT: any = 0;
function flash(msg) {
  var el = $('hMsg'); if (!el) return;
  el.textContent = msg; el.classList.add('show');
  clearTimeout(flashT);
  flashT = setTimeout(function () { el.classList.remove('show'); }, 1600);
}

function bind(rootId, sel, attr, fn) {
  var els = $(rootId).querySelectorAll(sel);
  for (var i = 0; i < els.length; i++) {
    (function (el) {
      el.addEventListener('click', function (e) {
        e.stopPropagation();
        var v = el.getAttribute(attr);
        if (v) fn(v);
      });
    })(els[i]);
  }
}
